import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { financialsApi } from '@/lib/financialsApi';
import { parseTypedAmount, toNumber } from '@/lib/number';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { ProfitabilityCard } from '@/components/financials/ProfitabilityCard';
import { CostBreakdownChart } from '@/components/financials/CostBreakdownChart';
import {
  ArrowLeft,
  Calendar,
  Plus,
  TrendingUp,
  Package,
} from 'lucide-react';
import type { ApiResponse, ApiListResponse } from '@/types/api';
import type {
  CycleFinancials,
  CostBreakdown,
  Adjustment,
  AdjustmentCreate,
} from '@/types/financials';
import { useMutation } from '@tanstack/react-query';
import { showSuccess, showError } from '@/lib/apiErrors';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/cn';
import { X } from 'lucide-react';
import { ADJUSTMENT_TYPE_OPTIONS } from '@/types/financials';

export const Route = createFileRoute('/_app/financials/cycles/$cycleId')({
  component: CycleFinancialsPage,
});

function formatPHP(value: number): string {
  return value.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' });
}

function CycleFinancialsPage() {
  const { cycleId } = Route.useParams();
  const { farmId, isLoading: isFarmLoading } = useCurrentFarm();

  const [adjustmentDrawerOpen, setAdjustmentDrawerOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery<ApiResponse<CycleFinancials>>({
    queryKey: ['financials', 'cycle', farmId, cycleId],
    queryFn: () => financialsApi.getCycleFinancials(farmId!, cycleId),
    staleTime: 60 * 1000,
    enabled: !!farmId,
  });

  const { data: breakdownData } = useQuery<ApiResponse<CostBreakdown>>({
    queryKey: ['financials', 'breakdown', farmId, cycleId],
    queryFn: () => financialsApi.getCostBreakdown(farmId!, cycleId),
    staleTime: 60 * 1000,
    enabled: !!farmId,
  });

  const { data: adjustmentsData, refetch: refetchAdjustments } = useQuery<ApiListResponse<Adjustment>>({
    queryKey: ['financials', 'adjustments', farmId, cycleId],
    queryFn: () => financialsApi.listAdjustments(farmId!, cycleId),
    staleTime: 60 * 1000,
    enabled: !!farmId,
  });

  const financials = data?.data;
  const breakdown = breakdownData?.data;
  const adjustments = adjustmentsData?.data ?? [];

  if (isFarmLoading || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading cycle financials...</p>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : 'Failed to load cycle financials'}
        onRetry={() => refetch()}
      />
    );
  }

  if (!financials) {
    return (
      <ErrorMessage message="Cycle financial data not found" onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link + header */}
      <div>
        <Link
          to="/financials"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to overview
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {financials.cycle_name || 'Cycle'}
              {financials.pond_name && (
                <span className="ml-2 text-base font-normal text-gray-500">
                  ({financials.pond_name})
                </span>
              )}
            </h2>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              {financials.status && <CycleStatusBadge status={financials.status} />}
              {financials.doc != null && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  DOC {financials.doc}
                </span>
              )}
              {financials.stocking_date && (
                <span>Started {new Date(financials.stocking_date).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profitability KPI cards */}
      <ProfitabilityCard
        costs={toNumber(financials.costs.total_cost)}
        revenue={toNumber(financials.revenue.total_revenue)}
        profit={toNumber(financials.profitability.gross_profit)}
        costPerKg={financials.profitability.cost_per_kg != null ? toNumber(financials.profitability.cost_per_kg) : null}
        revenuePerKg={financials.profitability.revenue_per_kg != null ? toNumber(financials.profitability.revenue_per_kg) : null}
        profitPerKg={financials.profitability.profit_per_kg != null ? toNumber(financials.profitability.profit_per_kg) : null}
        roiPct={financials.profitability.roi_pct}
        status={financials.profitability.status}
      />

      {/* Cost breakdown chart */}
      {breakdown && breakdown.by_category.length > 0 && (
        <CostBreakdownChart breakdown={breakdown} />
      )}

      {/* Revenue summary */}
      {financials.revenue.harvests.length > 0 && (
        <Card>
          <Card.Header>Revenue Summary</Card.Header>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-gray-500">
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium">Type</th>
                  <th className="px-5 py-3 font-medium text-right">Weight (kg)</th>
                  <th className="px-5 py-3 font-medium text-right">Price/kg</th>
                  <th className="px-5 py-3 font-medium text-right">Revenue</th>
                  <th className="px-5 py-3 font-medium">Grade</th>
                  <th className="px-5 py-3 font-medium">Buyer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {financials.revenue.harvests.map((harvest) => (
                  <tr key={harvest.id} className="hover:bg-gray-50/50">
                    <td className="px-5 py-3 text-gray-900">
                      {new Date(harvest.harvest_date).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 text-gray-600 capitalize">
                      {harvest.harvest_type}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-900">
                      {toNumber(harvest.weight_kg).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">
                      {harvest.price_per_kg != null ? formatPHP(toNumber(harvest.price_per_kg)) : '--'}
                    </td>
                    <td className="px-5 py-3 text-right font-medium text-green-600">
                      {harvest.revenue != null ? formatPHP(toNumber(harvest.revenue)) : '--'}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {harvest.size_grade || '--'}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {harvest.buyer || '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 bg-gray-50/50">
                  <td className="px-5 py-3 font-medium text-gray-900" colSpan={2}>
                    Total
                  </td>
                  <td className="px-5 py-3 text-right font-medium text-gray-900">
                    {toNumber(financials.revenue.total_weight_kg).toLocaleString()} kg
                  </td>
                  <td className="px-5 py-3 text-right text-gray-600">
                    {financials.revenue.avg_price_per_kg != null
                      ? formatPHP(toNumber(financials.revenue.avg_price_per_kg))
                      : '--'}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-green-700">
                    {formatPHP(toNumber(financials.revenue.total_revenue))}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {/* Projections */}
      {financials.projections && (
        <Card>
          <Card.Header>
            <span className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-gray-400" />
              Projections
            </span>
          </Card.Header>
          <Card.Body>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <ProjectionMetric
                label="Est. Remaining Harvest"
                value={financials.projections.estimated_remaining_harvest_kg}
                suffix="kg"
              />
              <ProjectionMetric
                label="Breakeven Price"
                value={financials.projections.breakeven_price_per_kg}
                format="php"
                suffix="/kg"
              />
              <ProjectionMetric
                label="Projected Revenue"
                value={financials.projections.projected_total_revenue_at_current_price}
                format="php"
              />
              <ProjectionMetric
                label="Projected Profit"
                value={financials.projections.projected_profit_at_current_price}
                format="php"
                colored
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Adjustments section */}
      <Card>
        <Card.Header
          action={
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setAdjustmentDrawerOpen(true)}
            >
              Add Adjustment
            </Button>
          }
        >
          Cost Adjustments
        </Card.Header>
        {adjustments.length === 0 ? (
          <Card.Body>
            <div className="flex flex-col items-center py-8 text-center">
              <Package className="h-8 w-8 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No adjustments recorded for this cycle.</p>
            </div>
          </Card.Body>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-gray-500">
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium">Type</th>
                  <th className="px-5 py-3 font-medium">Category</th>
                  <th className="px-5 py-3 font-medium text-right">Amount</th>
                  <th className="px-5 py-3 font-medium">Reason</th>
                  <th className="px-5 py-3 font-medium">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {adjustments.map((adj) => {
                  const amount = toNumber(adj.amount);
                  const isIncrease = adj.adjustment_type === 'increase';
                  return (
                    <tr key={adj.id} className="hover:bg-gray-50/50">
                      <td className="px-5 py-3 text-gray-600">
                        {new Date(adj.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3">
                        <Badge variant={isIncrease ? 'critical' : 'ok'} size="sm">
                          {isIncrease ? 'Increase' : 'Decrease'}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-gray-600 capitalize">
                        {adj.category_name || adj.category || 'General'}
                      </td>
                      <td className="px-5 py-3 text-right font-medium">
                        <span className={isIncrease ? 'text-red-600' : 'text-green-600'}>
                          {isIncrease ? '+' : '-'}{formatPHP(amount)}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-600 max-w-xs truncate">
                        {adj.reason}
                      </td>
                      <td className="px-5 py-3 text-gray-500">
                        {adj.created_by_name || '--'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Adjustment drawer */}
      {farmId && (
        <AdjustmentDrawer
          open={adjustmentDrawerOpen}
          onClose={() => setAdjustmentDrawerOpen(false)}
          farmId={farmId}
          cycleId={cycleId}
          onSuccess={() => {
            refetchAdjustments();
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function CycleStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'active':
      return <Badge variant="ok" dot>Active</Badge>;
    case 'completed':
    case 'harvested':
      return <Badge variant="success" dot>Completed</Badge>;
    case 'cancelled':
      return <Badge variant="critical" dot>Cancelled</Badge>;
    default:
      return <Badge variant="neutral">{status}</Badge>;
  }
}

interface ProjectionMetricProps {
  label: string;
  value: number | string | null;
  format?: 'php' | 'number';
  suffix?: string;
  colored?: boolean;
}

function ProjectionMetric({ label, value, format, suffix, colored }: ProjectionMetricProps) {
  const numVal = value != null ? toNumber(value) : null;
  let display: string;

  if (numVal == null) {
    display = '--';
  } else if (format === 'php') {
    display = formatPHP(numVal);
  } else {
    display = numVal.toLocaleString();
  }

  if (suffix && numVal != null) {
    display = `${display} ${suffix}`;
  }

  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p
        className={cn(
          'text-lg font-semibold mt-0.5',
          colored && numVal != null
            ? numVal >= 0
              ? 'text-emerald-700'
              : 'text-red-700'
            : 'text-gray-900'
        )}
      >
        {display}
      </p>
    </div>
  );
}

// ============================================================================
// Adjustment drawer
// ============================================================================

interface AdjustmentDrawerProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
  cycleId: string;
  onSuccess?: () => void;
}

function AdjustmentDrawer({ open, onClose, farmId, cycleId, onSuccess }: AdjustmentDrawerProps) {
  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/25 backdrop-blur-sm z-40 transition-opacity"
          onClick={onClose}
        />
      )}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full sm:w-[440px]',
          'bg-white shadow-xl border-l border-gray-200',
          'transform transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {open && (
          <AdjustmentForm
            farmId={farmId}
            cycleId={cycleId}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        )}
      </div>
    </>
  );
}

function AdjustmentForm({
  farmId,
  cycleId,
  onClose,
  onSuccess,
}: {
  farmId: string;
  cycleId: string;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const [adjustmentType, setAdjustmentType] = useState<string>('increase');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');

  const { data: categoriesData } = useQuery({
    queryKey: ['financials', 'categories'],
    queryFn: () => financialsApi.listCategories(),
    staleTime: 5 * 60 * 1000,
  });
  const categories = categoriesData?.data ?? [];

  const mutation = useMutation({
    mutationFn: (data: AdjustmentCreate) =>
      financialsApi.createAdjustment(farmId, cycleId, data),
    onSuccess: () => {
      showSuccess('Adjustment created');
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to create adjustment');
    },
  });

  // "12,5" is 12.5, "12,500" is 12500; anything else is refused with a message.
  const amountParsed = parseTypedAmount(amount, { thousands: true });
  const amountValue = 'value' in amountParsed ? amountParsed.value : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (amountValue === null || !reason.trim()) return;

    const data: AdjustmentCreate = {
      adjustment_type: adjustmentType as 'increase' | 'decrease',
      amount: amountValue,
      reason: reason.trim(),
      ...(category && { category }),
    };

    mutation.mutate(data);
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Add Cost Adjustment</h2>
          <p className="text-sm text-gray-500">Adjust cycle costs up or down</p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        <Select
          label="Adjustment Type"
          value={adjustmentType}
          onChange={(e) => setAdjustmentType(e.target.value)}
          options={[...ADJUSTMENT_TYPE_OPTIONS]}
        />

        <Input
          label="Amount (PHP)"
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={'error' in amountParsed ? amountParsed.error : undefined}
          placeholder="0.00"
          required
        />

        <Select
          label="Category (optional)"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={[
            { value: '', label: 'General' },
            ...categories.map((c) => ({ value: c.code, label: c.name })),
          ]}
        />

        <Textarea
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this adjustment needed?"
          rows={3}
        />
      </form>

      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center gap-3">
        <Button
          variant="primary"
          fullWidth
          loading={mutation.isPending}
          disabled={amountValue === null || amountValue <= 0 || !reason.trim()}
          onClick={handleSubmit as unknown as () => void}
        >
          Create Adjustment
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
